import axios from "axios";

import type { AttributionData } from "@fluid-app/api-client";
import {
  COOKIE_KEYS,
  STORAGE_KEYS,
  StorageManager,
} from "@fluid-app/storage-manager";

import type { FairshareConfig, TrackableEventWithData } from "./types";
import { DEFAULT_CONFIG } from "../utils/config";
import { debug, setDebugMode } from "../utils/debug";
import { retry } from "../utils/retry";
import { AttributionManager } from "./AttributionManager";
import { EventManager } from "./EventManager";
import { FingerPrintManager } from "./FingerPrintManager";
import { SessionManager } from "./SessionManager";

/**
 * Core implementation of the FairShare tracking library
 */
export class FairShareCore {
  private static instance: FairShareCore | null = null;
  protected config: FairshareConfig;
  protected sessionToken: string | null = null;
  protected fingerprint: string | null = null;
  private initialized = false;
  private lastPageUrl: string | null = null;
  private navigationIntervalId?: number;

  // Promise for tracking initialization status
  private initializationPromise: Promise<void>;
  private resolveInitialization?: () => void;

  // Managers
  private storageManager: StorageManager;
  private fingerPrintManager: FingerPrintManager;
  private sessionManager: SessionManager;
  private attributionManager: AttributionManager;
  private eventManager: EventManager;

  private constructor(config: FairshareConfig) {
    // Store the config directly
    this.config = config;

    // Initialize managers
    this.storageManager = new StorageManager();
    this.fingerPrintManager = new FingerPrintManager(this.storageManager);

    this.sessionManager = new SessionManager(
      this.storageManager,
      DEFAULT_CONFIG.RETRY_ATTEMPTS,
      this.config.fluidShop,
    );

    // Initialize server attribution manager (replacing AttributionManager)
    this.attributionManager = new AttributionManager(this.storageManager);

    this.eventManager = new EventManager(
      this.storageManager,
      this.sessionManager,
      this.attributionManager,
      this.config.fluidShop,
      DEFAULT_CONFIG.RETRY_ATTEMPTS,
      this.config,
    );

    // Set up initialization Promise
    this.initializationPromise = new Promise<void>((resolve) => {
      this.resolveInitialization = resolve;
    });
  }

  /**
   * Get the existing FairShare instance
   * @returns The FairShare instance
   * @throws Error if FairShare is not initialized
   */
  public static getInstance(): FairShareCore {
    if (!FairShareCore.instance) {
      throw new Error(
        "FairShare not initialized. Call initializeFairshare first.",
      );
    }
    return FairShareCore.instance;
  }

  /**
   * Create and initialize a new FairShare instance
   * @param config The FairShare configuration
   * @returns The initialized FairShare instance
   */
  public static createInstance(config: FairshareConfig): FairShareCore {
    // Create a new instance with the provided config
    const instance = new FairShareCore(config);
    FairShareCore.instance = instance;

    // Initialize the instance (mark it as initializing to avoid double initialization)
    instance.initialized = true;
    instance.initializeCore(config);

    return instance;
  }

  /**
   * Create a new instance of FairShareCore, replacing any existing instance
   * For internal use by the reset function
   */
  public static resetInstance(): void {
    FairShareCore.instance = null;
  }

  /**
   * Initialize the FairShare SDK core functionality
   * @param config Configuration options
   * @private Used internally by initializeFairshare and createInstance
   */
  private initializeCore(config: FairshareConfig): void {
    // Validate required configuration
    if (!config.fluidShop) {
      throw new Error("fluidShop is required in the configuration");
    }

    debug("FairShare configuration received");

    // Completely replace the config with the new one
    this.config = config;

    debug("FairShare initialized with config:", this.config);

    // Recreate the AttributionManager with the updated config
    this.attributionManager = new AttributionManager(this.storageManager);

    // Set debug mode
    setDebugMode(!!this.config.debug);

    // Update EventManager with fluidShop setting
    this.eventManager.setFluidShop(this.config.fluidShop);

    // Always use the default API endpoint
    axios.defaults.baseURL = DEFAULT_CONFIG.API_ENDPOINT;

    // Initialize fingerprint locally
    this.fingerprint = this.getStoredFingerprint();
    debug("Retrieved stored fingerprint:", this.fingerprint);

    if (this.fingerprint === null) {
      debug("No stored fingerprint found, generating a new one");
      this.generateFingerprint()
        .then((fingerprint) => {
          this.storeFingerprint(fingerprint);
          debug("Generated and stored new fingerprint:", this.fingerprint);
        })
        .catch((error) => {
          debug("Error generating fingerprint:", error);
        });
    } else {
      debug("Using existing stored fingerprint:", this.fingerprint);
    }

    // Set fingerprint in utils if available
    if (this.fingerprint) {
      this.fingerPrintManager.setFingerprint(this.fingerprint);
      debug("Set fingerprint in manager:", this.fingerprint);
    }

    // Initialize session first - this is an async operation but we'll handle dependencies correctly
    void this.initSession()
      .then(() => {
        // Check if session initialization was successful
        const sessionToken = this.sessionManager.getSessionToken();
        if (sessionToken) {
          debug("Session initialized successfully:", sessionToken);

          // NOTE: We no longer automatically track the initial page visit here
          // This allows FluidCore to control when initial tracking happens
          // after server attribution manager is set up
          debug("Session ready, waiting for external tracking trigger");

          // Resolve the initialization Promise only if session is ready
          if (this.resolveInitialization) {
            this.resolveInitialization();
          }
        } else {
          debug(
            "Session initialization completed but no session token available",
          );

          // Don't resolve initialization Promise without a session token
          // Instead, reject it so callers know initialization failed
          if (this.resolveInitialization) {
            // Actually, for backward compatibility, still resolve but log the issue
            // This allows the SDK to continue working even without a session
            this.resolveInitialization();
          }
        }
      })
      .catch((error) => {
        debug("Error initializing session:", error);

        // For backward compatibility, still resolve the initialization Promise
        // This ensures calling code can continue, but session-dependent features won't work
        if (this.resolveInitialization) {
          this.resolveInitialization();
        }
      });

    // Save current URL to avoid duplicate tracking
    if (typeof window !== "undefined") {
      this.lastPageUrl = window.location.href;
    }

    // Set up automatic page visit tracking for client-side navigation
    if (typeof window !== "undefined") {
      // Set up URL change detection
      this.startNavigationTracking();

      // Add unload event listener to flush queue on page exit
      window.addEventListener("beforeunload", () => {
        this.stopNavigationTracking();
        // Use beacon-based flushing for reliable delivery
        void this.eventManager.flushQueueWithBeacon();
      });
    }

    // Add additional event listeners for better mobile support
    if (typeof document !== "undefined") {
      // Handle page visibility changes (better mobile support)
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          // Page is being hidden, flush with beacon
          void this.eventManager.flushQueueWithBeacon();
        }
      });

      // Handle page freeze (modern browsers)
      window.addEventListener("freeze", () => {
        void this.eventManager.flushQueueWithBeacon();
      });
    }
  }

  /**
   * Initialize the FairShare SDK
   * @param config Configuration options
   */
  public initializeFairshare(config: FairshareConfig): void {
    if (this.initialized) {
      debug("FairShare already initialized");
      return;
    }

    this.initializeCore(config);
    this.initialized = true;
  }

  /**
   * Start tracking navigation changes for automatic page visit tracking
   */
  private startNavigationTracking(): void {
    if (typeof window === "undefined") return;

    // Track hash changes
    window.addEventListener("hashchange", this.boundHandleUrlChange);

    // Track popstate events (back/forward buttons)
    window.addEventListener("popstate", this.boundHandleUrlChange);

    // For client-side navigation (pushState/replaceState), use a polling approach
    this.navigationIntervalId = window.setInterval(this.checkUrlChange, 300);
  }

  /**
   * Stop tracking navigation changes
   */
  private stopNavigationTracking(): void {
    if (typeof window === "undefined") return;

    // Remove event listeners
    window.removeEventListener("hashchange", this.boundHandleUrlChange);
    window.removeEventListener("popstate", this.boundHandleUrlChange);

    // Clear the interval
    if (this.navigationIntervalId !== undefined) {
      window.clearInterval(this.navigationIntervalId);
      this.navigationIntervalId = undefined;
    }
  }

  /**
   * Bound version of handleUrlChange for use in event listeners
   */
  private boundHandleUrlChange = (): void => {
    this.handleUrlChange();
  };

  /**
   * Check if the URL has changed and handle it if it has
   */
  private checkUrlChange = (): void => {
    if (typeof window === "undefined") return;

    const currentUrl = window.location.href;
    if (currentUrl !== this.lastPageUrl) {
      this.handleUrlChange();
    }
  };

  /**
   * Handle URL changes and track page visits when the URL changes
   */
  private handleUrlChange(): void {
    if (typeof window === "undefined") return;

    const currentUrl = window.location.href;

    // Only track if the URL has changed
    if (currentUrl !== this.lastPageUrl) {
      // First update the lastPageUrl to prevent duplicate tracking
      this.lastPageUrl = currentUrl;

      // Capture attribution first, then track the visit to ensure fresh attribution data
      this.captureAttribution();
    }
  }

  protected async initSession(): Promise<void> {
    try {
      // First check if we can get an existing session token
      const existingToken = this.sessionManager.getSessionToken();

      if (existingToken) {
        debug("Using existing session token:", existingToken);
        this.sessionToken = existingToken;
        return;
      }

      // If no existing token, create a new session
      await this.sessionManager.initSession();
      this.sessionToken = this.sessionManager.getSessionToken();

      if (this.sessionToken) {
        debug("Successfully initialized session:", this.sessionToken);
      } else {
        debug(
          "Failed to initialize session - no token available after initialization",
        );
      }
    } catch (error) {
      debug("Error during session initialization:", error);
      // Don't throw here to prevent blocking initialization of other components
    }
  }

  public getSessionToken(): string | null {
    // First check if we have a token in memory
    if (this.sessionToken) {
      return this.sessionToken;
    }

    // Then try to get it from the session manager
    const token = this.sessionManager.getSessionToken();

    // Update our cached token if one was found
    if (token) {
      this.sessionToken = token;
    }

    return token;
  }

  protected async createSession(): Promise<string | null> {
    return this.sessionManager.createSession();
  }

  protected async generateFingerprint(): Promise<string> {
    return this.fingerPrintManager.generateFingerprint();
  }

  protected storeFingerprint(fingerprint: string): void {
    this.fingerPrintManager.storeFingerprint(fingerprint);
  }

  protected getStoredFingerprint(): string | null {
    return this.fingerPrintManager.getStoredFingerprint();
  }

  /**
   * Capture attribution data from the URL
   * @returns The captured attribution data or null if not found
   * @deprecated Server-side attribution is now handled via settings API.
   * This method maintained for backward compatibility but may return null.
   */
  public captureAttribution(): AttributionData | null {
    // Server attribution is managed externally via settings API
    // Return current effective attribution if available
    const attribution = this.attributionManager.getEffectiveAttribution();
    debug("FairShare: Current effective attribution:", attribution);
    return attribution;
  }

  /**
   * Get attribution data from the current URL
   * @returns The attribution data from URL or null if not found
   * @deprecated Server-side attribution is now handled via settings API.
   * Use getEffectiveAttribution() instead for current attribution data.
   */
  public getAttribution(): AttributionData | null {
    // Return current effective attribution from server attribution manager
    return this.attributionManager.getEffectiveAttribution();
  }

  public trackFairshareEvent(event: TrackableEventWithData): void {
    return this.eventManager.trackEvent(event);
  }

  /**
   * Track a checkout started event using the specific EventManager method
   * This ensures proper metadata building and processing
   * @param data Checkout started event data
   */
  public trackCheckoutStarted(data: { cart_token: string }): void {
    // The EventManager will rebuild metadata with proper attribution and session data
    // We just need to provide the minimal structure that satisfies the API type
    return this.eventManager.trackCheckoutStarted({
      cart_token: data.cart_token,
      metadata: {
        fluid_shop: "", // This will be rebuilt by EventManager
        fluid_session: "", // This will be rebuilt by EventManager
      },
    });
  }

  /**
   * Track a checkout started event synchronously
   * Returns a promise that resolves when the API request completes
   * @param data Checkout started event data
   */
  public async trackCheckoutStartedSync(data: {
    cart_token: string;
  }): Promise<void> {
    // The EventManager will rebuild metadata with proper attribution and session data
    // We just need to provide the minimal structure that satisfies the API type
    return this.eventManager.trackCheckoutStartedSync({
      cart_token: data.cart_token,
      metadata: {
        fluid_shop: "", // This will be rebuilt by EventManager
        fluid_session: "", // This will be rebuilt by EventManager
      },
    });
  }

  /**
   * Flush all pending events in the queue
   * @param sync Whether to wait for all events to complete processing before returning
   * @param timeoutMs Maximum time to wait for events to complete (only applies when sync=true)
   * @returns Promise that resolves when events are processed
   */
  public flushEvents(sync = true, timeoutMs = 3000): Promise<void> {
    return this.eventManager.flushQueue(sync, timeoutMs);
  }

  /**
   * Flush all pending events using navigator.sendBeacon for reliable delivery during page unload
   * @returns Promise that resolves when events are processed with beacon
   */
  public flushEventsWithBeacon(): Promise<void> {
    return this.eventManager.flushQueueWithBeacon();
  }

  protected getUserId(): string {
    return this.eventManager.getUserId();
  }

  protected debug(message: string, ...args: unknown[]): void {
    debug(message, ...args);
  }

  protected async retry<T>(
    fn: () => Promise<T>,
    attempts: number,
    delay = 1000,
  ): Promise<T> {
    return retry(fn, attempts, delay);
  }

  public reset(): void {
    // Clean up event listeners and intervals
    this.stopNavigationTracking();

    Object.values(STORAGE_KEYS).forEach((val) => {
      this.storageManager.removeItem({ key: val });
    });

    Object.values(COOKIE_KEYS).forEach((val) => {
      this.storageManager.removeItem({ key: val });
    });

    // Reset managers
    if (this.fingerprint) {
      this.fingerPrintManager.setFingerprint("");
    }
    this.sessionManager.setSessionToken(null);
    this.fingerprint = null;
    this.lastPageUrl = null;
  }

  /**
   * Get the initialization Promise
   * @returns Promise that resolves when initialization is complete
   */
  public getInitializationPromise(): Promise<void> {
    return this.initializationPromise;
  }
}
